/* eslint-disable react/prop-types */
import React, { useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { VideoDataContext } from ".";
import { generateUniqueID, getVideoAPIAndDomain } from "./utils";
import {
  setInitialVideoState,
  videoApiReducer
} from "../../hooks/useVideoApi/reducer";
import { useVideoApi } from "../../hooks/useVideoApi";

export const VideoDataProvider = ({
  uuid,
  video: initialVideoData,
  isSandbox,
  config = {},
  orcaRef,
  children,
  useVideoApiOptions = {}
}) => {
  const { adOverrides: { domain: domainOverride } = {} } = config;

  const videoProp = useMemo(() => {
    return initialVideoData || {};
  }, [initialVideoData]);

  const { videoApiUrl, domain } = useMemo(() => {
    return getVideoAPIAndDomain(domainOverride, isSandbox);
  }, [domainOverride, isSandbox]);

  const [videoDataState, dispatchVideoApiEvent] = useReducer(
    videoApiReducer,
    setInitialVideoState(videoProp)
  );

  const {
    originalVideo,
    videoApiDataFailed,
    videoData,
    fetchedByUUID,
    isForceLoadVideo
  } = videoDataState;

  const deps = {
    dispatchVideoApiEvent,
    domain,
    originalVideoData: originalVideo,
    uuid,
    videoApiUrl,
    currentVideoData: videoData,
    propVideoData: videoProp,
    config,
    orcaRef
  };

  useVideoApi(deps, useVideoApiOptions);

  const videoId = useMemo(() => {
    return videoData?._id ? videoData._id : uuid;
  }, [videoData?._id, uuid]);

  // Used in cases where we need to differentiate 2 videos w/ the same id on one page
  const uniqueId = useMemo(() => {
    return generateUniqueID();
    // We want to update the unique ID when videoId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const contextValue = useMemo(
    () => ({
      originalVideo,
      videoApiDataFailed,
      videoData,
      videoId,
      dispatchVideoApiEvent,
      videoApiUrl,
      domain,
      fetchedByUUID,
      uniqueId,
      isForceLoadVideo
    }),
    [
      domain,
      originalVideo,
      videoApiDataFailed,
      videoApiUrl,
      videoData,
      videoId,
      fetchedByUUID,
      uniqueId,
      isForceLoadVideo
    ]
  );

  return (
    <VideoDataContext.Provider value={contextValue}>
      {children}
    </VideoDataContext.Provider>
  );
};

VideoDataProvider.propTypes = {
  video: PropTypes.object,
  domainOverride: PropTypes.string,
  isSandbox: PropTypes.bool,
  debug: PropTypes.bool,
  uuid: PropTypes.string,
  useVideoApiOptions: PropTypes.object,
  children: PropTypes.node
};
